import * as React from "react";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  setAddFields,
  setCombinationUpsaleData,
  setCombinationUpsaleProductsQTY,
  setExtraFieldsData,
  setGuestDetailsData,
  setGuestFields,
  setIsBackToHoreca,
  setQuantities,
  setSelectedCombinationExtraProduct,
  setSelectedExtraProduct,
  setTransactionDetails,
  toggleBookNowOpen,
} from "../../store/slice/Addguest.slice";
import {
  StepConnector,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  createReservation,
  getExtraFieldDetails,
  getGuestDetails,
  transactionCostForReservation,
} from "../../api/HorecaWidget.service";
import { Formik } from "formik";
import * as Yup from "yup";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Snackbar from "@mui/material/Snackbar";
import dayjs from "dayjs";
import Combination from "../Component/Combination";
import UpsaleProduct from "../Component/UpsaleProduct";
import UserDetailsSection from "../Component/UserDetailSection";
import InactivityPopup from "../../utils/InActivityPopup";
import UpsaleProductCombination from "../Component/UpsaleProductCombination";

dayjs?.extend(customParseFormat);

// ─── Inject autofill CSS once ─────────────────────────────────────────────────
// Makes the browser fire onAnimationStart when it autofills an input.
const injectAutofillStyles = () => {
  if (document.getElementById("autofill-keyframes")) return;
  const style = document.createElement("style");
  style.id = "autofill-keyframes";
  style.innerHTML = `
    @keyframes onAutoFillStart  { from {} to {} }
    @keyframes onAutoFillCancel { from {} to {} }
    input:-webkit-autofill {
      animation-name: onAutoFillStart;
      animation-duration: 0.001s;
    }
    input:not(:-webkit-autofill) {
      animation-name: onAutoFillCancel;
      animation-duration: 0.001s;
    }
  `;
  document.head.appendChild(style);
};

function useAutofillSync(containerRef, setFieldValue, setFieldTouched, validateForm) {
  const pollingRef = React.useRef(null);
  // Track already-synced field:value pairs to avoid redundant Formik updates
  const syncedRef = React.useRef(new Set());

  const syncAllInputs = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const allInputs = container.querySelectorAll("input");
    let didSync = false;

    allInputs.forEach((input) => {
      const name = input.name || input.getAttribute("name");
      const value = input.value;
      if (!name || !value) return;

      const key = `${name}:${value}`;
      if (syncedRef.current.has(key)) return; // already synced this exact value

      syncedRef.current.add(key);
      // false = don't validate per-field yet, we do one batch validateForm below
      setFieldValue(name, value, false);
      setFieldTouched(name, true, false);
      didSync = true;
    });

    if (didSync) {
      // Single validateForm call clears ALL errors at once
      setTimeout(() => validateForm(), 0);
    }
  }, [containerRef, setFieldValue, setFieldTouched, validateForm]);

  // Strategy 1: animation event (Chrome/Edge)
  const handleAnimationStart = React.useCallback(
    (e) => {
      if (e.animationName !== "onAutoFillStart") return;
      // Small delay so the browser finishes filling all fields in the batch
      setTimeout(syncAllInputs, 50);
    },
    [syncAllInputs]
  );

  // Strategy 2: polling (Firefox, Safari, slow autofill)
  const startPolling = React.useCallback(() => {
    stopPolling(); // clear any existing interval first
    let attempts = 0;
    pollingRef.current = setInterval(() => {
      syncAllInputs();
      attempts++;
      if (attempts >= 6) clearInterval(pollingRef.current);
    }, 500);
  }, [syncAllInputs]);

  const stopPolling = React.useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
  }, []);

  // Clear the sync cache when the hook re-initialises (step change)
  React.useEffect(() => {
    syncedRef.current = new Set();
    return () => stopPolling();
  }, [stopPolling]);

  return { handleAnimationStart, startPolling, stopPolling };
}

const DottedStepConnector = (props) => (
  <StepConnector
    {...props}
    sx={{
      "& .MuiStepConnector-line": {
        borderColor: props?.color ?? "#00000",
        borderStyle: "dashed",
        borderWidth: 1,
      },
    }}
  />
);

export default function BookNowForm() {
  const stepContentRef = React.useRef(null);
  const { combinationList, selectedCombination } = useSelector(
    (store) => store.Activity
  );
  const { css } = useSelector((store) => store?.widgetSettings);
  const bookBtnCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Overall"
  )?.fields;
  const {
    reservType,
    GuestDetailsData,
    extraProductData,
    quantities,
    reservGroup,
    adult,
    child,
    kid,
    baby,
    toddler,
    selectedDate,
    time,
    totalCount,
    selectedExtraProduct,
    transactionDetails,
    guestFields,
    addFields,
    languageWords,
    defaultLanguage,
    extraFieldsData,
    totalInclusiveVAT,
    combinationUpsaleProductsQTY,
    selectedCombinationUpsale,
    combinationUpsaleData,
    totalCostYourBooking,
    languageId,
  } = useSelector((store) => store.addGuest);

  const [steps, setSteps] = React.useState([
    languageWords?.Availability,
    languageWords?.ExtraProducts,
    languageWords?.combinations,
    languageWords?.yourDetails,
    languageWords?.ConfirmandPay,
  ]);
  const [isLoading, setIsLoading] = React.useState();
  const theme = useTheme();
  const {
    WidgetSetting,
    key,
    floating,
    resType,
    widgetConfig,
    guestId,
    deviceId,
  } = useSelector((store) => store?.widgetSettings);
  const { defaultLang } = useSelector((store) => store?.Combination);
  const { combinationQTY } = useSelector((store) => store?.Activity);

  const VAT_PERCENT_PRODUCTS = reservType?.vat;
  const VAT_TOTAL_PRODUCTS =
    reservType?.totalCost -
    reservType?.totalCost / (1 + VAT_PERCENT_PRODUCTS / 100);
  const VAT_TOTAL_EXTRA = selectedExtraProduct?.reduce((acc, product) => {
    return (
      (acc + (product?.price - product?.price / (1 + product?.vat / 100))) *
      product.quantity
    );
  }, 0);
  const VAT_TOTAL_COMBINATIONS = selectedCombination?.reduce((acc, product) => {
    return (
      (acc + (product?.price - product?.price / (1 + product?.vat / 100))) *
      combinationQTY[product?.idreservationtype + String(product.isBefore)]
    );
  }, 0);
  const VAT_TOTAL_COMBINATIONS_UPSALE =
    selectedCombinationUpsale?.reduce((acc, product) => {
      return (
        (acc + (product?.price - product?.price / (1 + product?.vat / 100))) *
        product.quantity
      );
    }, 0) || 0;
  const VAT_TOTAL_TRANSACTION_COST =
    (transactionDetails?.pricePerReservation -
      transactionDetails?.pricePerReservation /
        (1 + transactionDetails?.vat / 100)) *
    1;
  const TOTAL_VAT = (
    VAT_TOTAL_PRODUCTS +
    VAT_TOTAL_EXTRA +
    VAT_TOTAL_TRANSACTION_COST +
    VAT_TOTAL_COMBINATIONS +
    VAT_TOTAL_COMBINATIONS_UPSALE
  ).toFixed(2);

  const totalSum = selectedExtraProduct?.reduce(
    (acc, product) => acc + product.quantity * product.price,
    0
  );
  const totalCombination = selectedCombination?.reduce(
    (acc, product) =>
      acc +
      product?.price *
        combinationQTY[product?.idreservationtype + String(product.isBefore)],
    0
  );
  const totalUpsaleCombinationPrice =
    selectedCombinationUpsale?.reduce(
      (acc, product) =>
        acc + product?.price * combinationUpsaleProductsQTY[product?.idproduct],
      0
    ) || 0;

  let TOTAL_INCLUSIVE_VAT =
    (!reservType?.totalCost || reservType?.totalCost > 0
      ? reservType?.totalCost
      : 0) +
    totalSum +
    totalCombination +
    totalUpsaleCombinationPrice;
  TOTAL_INCLUSIVE_VAT =
    TOTAL_INCLUSIVE_VAT > 0
      ? TOTAL_INCLUSIVE_VAT + transactionDetails?.pricePerReservation
      : TOTAL_INCLUSIVE_VAT;

  const TOTAL_PRODUCT_PRICE =
    (reservType?.totalCost != null ||
    !reservType?.totalCost ||
    reservType?.totalCost > 0
      ? reservType?.totalCost
      : 0) +
    totalSum +
    totalCombination +
    totalUpsaleCombinationPrice;

  const reservationRequestMin =
    parseInt(
      WidgetSetting?.find((item) => item?.tag === "reservationRequest")
    ) ?? 0;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(1);
  const [completed, setCompleted] = React.useState({ 0: false });
  const dispatch = useDispatch();
  const totalSteps = () => steps.length;
  const [state, setState] = React.useState({
    open: false,
    vertical: "top",
    horizontal: "right",
  });
  const { vertical, horizontal, open } = state;

  const handleClosebar = () => {
    setState({ ...state, open: true });
    setTimeout(() => setState({ ...state, open: false }), 5000);
  };

  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    if (activeStep == 1) {
      dispatch(setIsBackToHoreca(true));
      navigate(-1);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleNextFunc = (errors) => {
    if (steps[activeStep] === languageWords?.yourDetails) {
      if (Object.keys(errors).length == 0) creatBookingReservation();
    } else {
      handleNext();
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
  };

  const fetchGuestDetails = async () => {
    try {
      const response = await getGuestDetails(
        key,
        reservType?.idreservationtype,
        defaultLang ? defaultLang : defaultLanguage
      );
      if (response?.status != 200 || !response?.data) {
        console.error("Failed to fetch guest details:", response);
        return;
      }
      dispatch(setGuestDetailsData(response?.data));
    } catch (error) {
      console.error(error);
    }
  };

  const fetchExtraFieldDetails = async () => {
    try {
      const response = await getExtraFieldDetails(
        reservType?.idreservationtype,
        defaultLang ? defaultLang : defaultLanguage,
        key
      );
      dispatch(setExtraFieldsData(response));
    } catch (error) {
      console.error(error);
    }
  };

  const creatBookingReservation = async () => {
    setIsLoading(true);
    const parsedDate = dayjs(selectedDate, "DD-MM-YYYY", true);
    if (!parsedDate?.isValid()) return;
    const formattedDate = parsedDate.format("YYYY-MM-DD");

    const upsaleproduct = selectedExtraProduct?.map((item) => ({
      idupsaleproduct: item?.idproduct,
      quantity: item?.quantity,
      price: item.price * item?.quantity,
      supportingproducts: null,
    }));

    const guestPayload = guestFields.reduce((acc, item) => {
      const k = item.idfield.replace(/{|}/g, "");
      acc[k] = item.data;
      return acc;
    }, {});

    const combinationInfoPayload = selectedCombination?.map((item) => {
      let count = combinationQTY[item?.idreservationtype + String(item.isBefore)];
      const combinationUpsalePrice =
        selectedCombinationUpsale?.reduce((acc, i) => acc + i.quantity * i.price, 0) || 0;

      const selectedTables = (() => {
        if (item?.activitiesPriceType === "pricepertime") {
          return item?.tables?.slice(0, count);
        }
        const singleTable = item?.tables?.find((t) => t.seats >= count);
        if (singleTable) return [singleTable];
        let seatSum = 0;
        let tablesPicked = [];
        for (let table of item?.tables) {
          tablesPicked.push(table);
          seatSum += table.seats;
          if (seatSum >= count) break;
        }
        return tablesPicked;
      })();

      return {
        combinationUpsaleProduct: selectedCombinationUpsale?.filter(
          (prod) =>
            prod?.isBefore == item?.isBefore &&
            prod.idreservationtype == item.idreservationtype
        ),
        idreservationtype: item?.idreservationtype,
        EventTicketDateTimeId:
          item?.eventTicketDateTimeId == 0 ? null : item?.eventTicketDateTimeId,
        ArrivalDate: formattedDate,
        DepartureDate: formattedDate,
        ArrivalTime: item?.arrivalTime,
        DepartureTime: item?.depatureTime,
        vat: item?.vat,
        VATAmount: Number(
          parseFloat(VAT_TOTAL_COMBINATIONS + VAT_TOTAL_COMBINATIONS_UPSALE).toFixed(2)
        ),
        PaymentAmount: count * item?.price + combinationUpsalePrice,
        ReservationLocations:
          selectedTables?.map((table) => ({
            idtables: table?.idtables,
            ArrivalDate: formattedDate,
            DepartureDate: formattedDate,
            ArrivalTime: item?.arrivalTime,
            DepartureTime: item?.depatureTime,
            seats: table?.seats,
          })) || [],
        MainModuleId: item?.mainModuleId,
        Guests: [guestPayload],
        numberOfGuest:
          item?.activitiesPriceType == "pricepertime"
            ? totalCount
            : combinationQTY[item?.idreservationtype + String(item.isBefore)],
        LanguageCode: defaultLanguage,
      };
    });

    const payload = {
      KioskDeviceId: deviceId,
      WidgetId: Number(widgetConfig?.settings?.idwidgetsetup),
      SumOfPaymentAmount: Number(parseFloat(TOTAL_INCLUSIVE_VAT).toFixed(2)),
      paymentAmount: reservType?.totalCost + totalSum,
      kioskMode: widgetConfig?.settings?.kioskMode,
      idreservationtype: reservType?.idreservationtype,
      idarrangementtype: reservGroup?.idarrangementtype,
      field1: parseInt(adult) || 0,
      field2: parseInt(child) || 0,
      field3: parseInt(baby) || 0,
      field4: parseInt(toddler) || 0,
      field5: parseInt(kid) || 0,
      arrivaldate: formattedDate,
      arrivaltime: `${time};${reservType?.duration};${reservType.durationgroups}`,
      key: key,
      language: defaultLang ? defaultLang : defaultLanguage,
      newsletter: false,
      reservationRequest: totalCount > widgetConfig?.reservationRequestMin,
      guestFields: guestFields || [],
      fields: addFields,
      app: false,
      horecaProductReservation: {
        idhorecaproduct: reservType?.idproducthoreca ?? 0,
        numberofguests: totalCount,
        price:
          totalCount > widgetConfig?.reservationRequestMin
            ? 0
            : resType?.totalCost * totalCount ?? 0,
      },
      upsaleProductReservations: upsaleproduct || [],
      VATAmount: Number(
        parseFloat(VAT_TOTAL_PRODUCTS + VAT_TOTAL_EXTRA + VAT_TOTAL_TRANSACTION_COST).toFixed(2)
      ),
      transactionCostReservation: {
        idproduct: transactionDetails?.idproduct,
        idreservation: reservType?.idreservationtype,
        price: transactionDetails?.pricePerReservation,
      },
      baseUrl: "",
      isGroupReservation: false,
      reservationRequestMin: reservationRequestMin?.data,
      MainModuleId: 1,
      ...(guestId ? { guestId } : {}),
      CombinationInfo: combinationInfoPayload,
    };

    try {
      const response = await createReservation(payload);
      if (response?.status === 200) {
        const checkoutUrl = response?.data?._links?.checkout?.href;
        if (widgetConfig?.settings?.kioskMode || deviceId) {
          console.info("window parent1")
          window.location.href = checkoutUrl;
        } else if (window.parent && window.parent !== window) {
          console.info("window parent2")
          window.parent.postMessage({ redirectUrl: checkoutUrl }, "*");
        } else {
          console.info("window parent3")
          window.location.href = checkoutUrl;
        }
      } else {
        handleClosebar();
      }
      setIsLoading(false);
    } catch (error) {
      console.info(error);
      setIsLoading(false);
    }
  };

  const fetchTransactionDetails = async () => {
    try {
      const response = await transactionCostForReservation(
        key,
        reservType?.idreservationtype,
        defaultLang ? defaultLang : defaultLanguage
      );
      if (response?.status != 200 || !response?.data) {
        console.error("Transaction details not found");
      }
      dispatch(setTransactionDetails(response?.data));
    } catch (error) {
      console.error("error in transaction cost:", error?.message);
    }
  };

  const createInitialValuesAndValidationSchema = (guestData, extraData, widgetConfig) => {
    const initialValues = {};
    const validationShape = {};

    const processFieldData = (field, languageType, customLink, shouldApplyValidation) => {
      let languageData = field[languageType]?.find((f) => f?.fieldname == "name")?.data;

      if (customLink) {
        languageData = "Link";
      } else if (field?.type == "textarea") {
        if (typeof field[languageType]?.[0]?.data === "string") {
          languageData = field[languageType]?.find((f) => f?.fieldname == "name")?.data?.replace(".", "");
        } else {
          languageData = field[languageType]?.find((f) => f?.fieldname == "name")?.data;
        }
      }

      if (!languageData) return;

      initialValues[languageData] = field?.type == "checkboxList" ? [] : "";

      let validationRule;
      if (field?.type === "label" || field?.type === "link") {
        validationRule = Yup.mixed().notRequired();
      } else if (field.tag === "{email}") {
        validationRule = Yup.string().email("Invalid email format");
      } else if (field.tag === "{phone}") {
        validationRule = Yup.string().matches(
          /^\+?\d{1,3}\s*\(?\d{1,4}?\)?[\s.-]?\d{1,4}[\s.-]?\d{1,4}[\s.-]?\d{1,9}$/,
          "Invalid phone number format"
        );
      } else if (field?.type === "termsandcondition") {
        validationRule = Yup.boolean().oneOf([true], "You must accept the terms and conditions");
      } else if (field?.type == "checkboxList") {
        validationRule = Yup.array().of(Yup.string());
      } else {
        validationRule = Yup.string();
      }

      if (shouldApplyValidation && field.required && field?.type !== "label" && field?.type !== "link") {
        validationRule = validationRule.required("Required");
      }
      validationShape[languageData] = validationRule;
    };

    guestData?.forEach((field) =>
      processFieldData(field, "guestLanguages", field.type == "link", !widgetConfig?.token)
    );
    extraData?.forEach((field) =>
      processFieldData(field, "fieldsLanguages", field.type == "link", true)
    );

    return { initialValues, validationSchema: Yup.object().shape(validationShape) };
  };

  const { initialValues, validationSchema } = createInitialValuesAndValidationSchema(
    GuestDetailsData,
    extraFieldsData,
    widgetConfig
  );

  React.useEffect(() => {
    injectAutofillStyles();
    fetchGuestDetails();
    fetchTransactionDetails();
    fetchExtraFieldDetails();
  }, []);

  const send = (frameHeight, frameWidth, top, right, bottom, left, floating, redirectUrl, scrollToTop) => {
    window.parent.postMessage(
      {
        reservationIframeHeight: frameHeight,
        reservationIframeWidth: frameWidth,
        reservationIframePositionTop: top,
        reservationIframePositionRight: right,
        reservationIframePositionBottom: bottom,
        reservationIframePositionLeft: left,
        reservationIframePositionFloating: floating,
        redirectUrl: redirectUrl,
        scrollToTop: scrollToTop ?? false,   // ← add this
      },
      "*"
    );
  };

  const updateWidgetHeight = () => {
    if (!stepContentRef.current) return;
    const contentHeight = Math.ceil(stepContentRef.current.scrollHeight);
    send(`${contentHeight}px`, "100%", "", "", "", "", widgetConfig?.settings?.floatButtonActive);
  };

  const scrollToTop = () => {
    // Tell parent to scroll — this is the iOS fix
    window.parent.postMessage({ scrollToTop: true }, "*");
  };

  React.useLayoutEffect(() => {
    // Small delay so DOM has painted before measuring height
    const t = setTimeout(() => {
      updateWidgetHeight();
      scrollToTop();
    }, 50);
    return () => clearTimeout(t);
  }, [activeStep, steps]);

  React.useEffect(() => {
    if (!stepContentRef.current) return;
    const observer = new ResizeObserver(() => updateWidgetHeight());
    observer.observe(stepContentRef.current);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (extraProductData?.length == 0 && combinationList.length == 0) {
      setSteps([languageWords?.Availability, languageWords?.yourDetails, languageWords?.ConfirmandPay]);
    } else if (extraProductData?.length == 0 && combinationList.length > 0) {
      setSteps([languageWords?.Availability, languageWords?.combinations, languageWords?.yourDetails, languageWords?.ConfirmandPay]);
    }
  }, [extraProductData]);

  React.useEffect(() => {
    document.body.setAttribute(
      "style",
      "background-color: " + (theme?.widget?.stepScreenBgColor ?? "#f2f5f4") + " !important"
    );
    if (combinationList.length == 0 && extraProductData?.length == 0) {
      setSteps([languageWords?.Availability, languageWords?.yourDetails, languageWords?.ConfirmandPay]);
    } else if (combinationList.length == 0 && extraProductData?.length != 0) {
      setSteps([languageWords?.Availability, languageWords?.ExtraProducts, languageWords?.yourDetails, languageWords?.ConfirmandPay]);
    }
  }, [combinationList]);

  React.useEffect(() => {
    if (totalCount >= widgetConfig?.reservationRequestMin) {
      setSteps([languageWords?.Availability, languageWords?.yourDetails, languageWords?.ConfirmandPay]);
    }
  }, [totalCount]);

  React.useEffect(() => {
    if (combinationList?.length > 0 && combinationUpsaleData?.length != 0 && extraProductData?.length != 0) {
      setSteps([
        languageWords?.Availability, languageWords?.ExtraProducts, languageWords?.combinations,
        languageWords?.combination_extraproduct || "Combination Extra Product",
        languageWords?.yourDetails, languageWords?.ConfirmandPay,
      ]);
    } else if (combinationList?.length > 0 && combinationUpsaleData?.length > 0 && extraProductData?.length == 0) {
      setSteps([
        languageWords?.Availability, languageWords?.combinations,
        languageWords?.combination_extraproduct || "Combination Extra Product",
        languageWords?.yourDetails, languageWords?.ConfirmandPay,
      ]);
    } else if (combinationList?.length > 0 && selectedCombination?.length == 0 && extraProductData?.length != 0) {
      setSteps([
        languageWords?.Availability, languageWords?.ExtraProducts, languageWords?.combinations,
        languageWords?.yourDetails, languageWords?.ConfirmandPay,
      ]);
    } else if (combinationList?.length > 0 && selectedCombination?.length == 0 && extraProductData?.length == 0) {
      setSteps([
        languageWords?.Availability, languageWords?.combinations,
        languageWords?.yourDetails, languageWords?.ConfirmandPay,
      ]);
    }
  }, [selectedCombination, combinationUpsaleData]);

  return (
    <>
      {deviceId && <InactivityPopup languageWords={languageWords} />}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {}}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          touched,
          errors,
          dirty,
          setFieldTouched,
          setFieldValue,
          validateForm,
        }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const { handleAnimationStart, startPolling, stopPolling } = useAutofillSync(
            stepContentRef,
            setFieldValue,
            setFieldTouched,
            validateForm
          );

          // Start polling whenever the user details step becomes active
          // eslint-disable-next-line react-hooks/rules-of-hooks
          React.useEffect(() => {
            if (steps[activeStep] === languageWords?.yourDetails) {
              startPolling();
            }
            return () => stopPolling();
          }, [activeStep, steps]);

          return (
            <Box
              ref={stepContentRef}
              id='step-content'
              onAnimationStart={handleAnimationStart}
              // sx={{ backgroundColor: theme?.widget?.stepScreenBgColor, p: 1,  // ADD THESE:
              // overflowY: "auto",
              // height: "100dvh",      // or "100dvh" for mobile browsers
              // boxSizing: "border-box", }}
              sx={{ 
                backgroundColor: theme?.widget?.stepScreenBgColor, 
                p: 1,
              }}
            >
              <Box sx={{ backgroundColor: theme?.widget?.stepScreenBgColor }}>
                <Box sx={{ width: "100%", px: 2, mb: 5 }}>
                  <Stepper
                    nonLinear
                    activeStep={activeStep}
                    connector={<DottedStepConnector color={theme?.widget?.stepperBorderColor} />}
                    sx={{ mt: 1, justifyContent: { xs: "center", sm: "flex-start" } }}
                  >
                    {steps.map((label, index) => (
                      <Step key={label} completed={completed[index]}>
                        <StepButton
                          sx={{
                            ...(activeStep === index && {
                              "& .MuiStepLabel-label": {
                                color: activeStep === index
                                  ? bookBtnCss?.stepTextColorActive
                                  : bookBtnCss?.stepTextColor,
                                fontWeight: "bold",
                                fontSize: "15px",
                              },
                              "& .MuiStepIcon-root": {
                                color: activeStep === index
                                  ? bookBtnCss?.stepCircleActiveBg
                                  : bookBtnCss?.stepBtnDisabledBg,
                                borderRadius: "50%",
                                "& .MuiStepIcon-text": {
                                  fill: activeStep === index
                                    ? bookBtnCss?.stepCircleTextActive
                                    : bookBtnCss?.stepBtnText,
                                },
                              },
                            }),
                            width: { xs: "100%", sm: "auto" },
                            fontSize: { xs: "12px", sm: "inherit" },
                          }}
                          disabled={true}
                        >
                          {isSmallScreen ? "" : label}
                        </StepButton>
                      </Step>
                    ))}
                  </Stepper>
                </Box>

                <div>
                  {allStepsCompleted() ? (
                    <React.Fragment>
                      <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                        <Box sx={{ flex: "1 1 auto" }} />
                        <Button onClick={handleReset}>Reset</Button>
                      </Box>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <Box>
                        {(() => {
                          switch (steps[activeStep]) {
                            case languageWords?.Availability:
                              navigate(-1, { state: { openWidgetScreen: true } });
                              break;

                            case languageWords?.ExtraProducts:
                              return (
                                <UpsaleProduct
                                  steps={steps} activeStep={activeStep}
                                  handleBack={handleBack} handleNextFunc={handleNextFunc}
                                  price={totalInclusiveVAT} errors={errors} isLoading={isLoading}
                                  languageId={languageId} selectedExtraProduct={selectedExtraProduct}
                                  extraProductData={extraProductData} quantities={quantities}
                                  selectedCombination={selectedCombination} totalCount={totalCount}
                                  languageWords={languageWords} reservType={reservType}
                                  selectedDate={selectedDate} adult={adult} child={child}
                                  kid={kid} baby={baby} toddler={toddler} time={time}
                                  transactionDetails={transactionDetails}
                                  transactionPrice={transactionDetails?.pricePerReservation}
                                  excludingVat={TOTAL_INCLUSIVE_VAT - TOTAL_VAT - transactionDetails?.pricePerReservation}
                                  totalVat={TOTAL_VAT} totalIncludingVAT={TOTAL_INCLUSIVE_VAT}
                                  totalCostYourBooking={reservType?.totalCost + totalSum}
                                  setQuantities={setQuantities} setSelectedExtraProduct={setSelectedExtraProduct}
                                  combinationUpsale={selectedCombinationUpsale}
                                  setCombinationUpsale={setSelectedCombinationExtraProduct}
                                  total_product_price={TOTAL_PRODUCT_PRICE}
                                />
                              );

                            case languageWords?.combinations:
                              return (
                                <Combination
                                  steps={steps} activeStep={activeStep}
                                  handleBack={handleBack} handleNextFunc={handleNextFunc}
                                  price={totalInclusiveVAT} errors={errors} isLoading={isLoading}
                                  languageId={languageId} totalCount={totalCount}
                                  languageWords={languageWords} reservType={reservType}
                                  selectedDate={selectedDate} time={time} adult={adult}
                                  child={child} kid={kid} baby={baby} toddler={toddler}
                                  selectedExtraProduct={selectedExtraProduct}
                                  transactionDetails={transactionDetails}
                                  transactionPrice={transactionDetails?.pricePerReservation}
                                  excludingVat={TOTAL_INCLUSIVE_VAT - TOTAL_VAT - transactionDetails?.pricePerReservation}
                                  totalVat={TOTAL_VAT} totalIncludingVAT={TOTAL_INCLUSIVE_VAT}
                                  totalCostYourBooking={reservType?.totalCost + totalSum}
                                  widgetType={"horeca"} setUpsaleCombination={setCombinationUpsaleData}
                                  combinationUpsale={selectedCombinationUpsale}
                                  setCombinationUpsale={setSelectedCombinationExtraProduct}
                                  total_product_price={TOTAL_PRODUCT_PRICE}
                                />
                              );

                            case languageWords?.combinationUpsaleProduct || "Combination Extra Product":
                              return (
                                <UpsaleProductCombination
                                  steps={steps} activeStep={activeStep}
                                  handleBack={handleBack} handleNextFunc={handleNextFunc}
                                  price={totalInclusiveVAT} errors={errors} isLoading={isLoading}
                                  languageId={languageId} selectedExtraProduct={selectedExtraProduct}
                                  extraProductData={extraProductData} selectedCombination={selectedCombination}
                                  totalCount={totalCount} languageWords={languageWords} reservType={reservType}
                                  selectedDate={selectedDate} adult={adult} child={child}
                                  kid={kid} baby={baby} toddler={toddler} time={time}
                                  transactionDetails={transactionDetails}
                                  transactionPrice={transactionDetails?.pricePerReservation}
                                  excludingVat={TOTAL_INCLUSIVE_VAT - TOTAL_VAT - transactionDetails?.pricePerReservation}
                                  totalVat={TOTAL_VAT} totalIncludingVAT={TOTAL_INCLUSIVE_VAT}
                                  totalCostYourBooking={reservType?.totalCost + totalSum}
                                  setSelectedExtraProduct={setSelectedCombinationExtraProduct}
                                  selectedUpsaleCombination={selectedCombinationUpsale}
                                  qty={combinationUpsaleProductsQTY} setQty={setCombinationUpsaleProductsQTY}
                                  UpsaleCombinationData={combinationUpsaleData}
                                  combinationUpsale={selectedCombinationUpsale}
                                  setCombinationUpsale={setSelectedCombinationExtraProduct}
                                  total_product_price={TOTAL_PRODUCT_PRICE}
                                />
                              );

                            case languageWords?.yourDetails:
                              return (
                                <UserDetailsSection
                                  steps={steps} activeStep={activeStep}
                                  handleBack={handleBack} handleNextFunc={handleNextFunc}
                                  price={totalInclusiveVAT} isLoading={isLoading}
                                  languageId={languageId} defaultLanguage={defaultLanguage}
                                  values={values} setField={setFieldValue}
                                  errors={errors} touched={touched} totalCount={totalCount}
                                  languageWords={languageWords} reservType={reservType}
                                  selectedDate={selectedDate} time={time} adult={adult}
                                  child={child} kid={kid} baby={baby} toddler={toddler}
                                  selectedExtraProduct={selectedExtraProduct}
                                  transactionDetails={transactionDetails}
                                  transactionPrice={transactionDetails?.pricePerReservation}
                                  excludingVat={TOTAL_INCLUSIVE_VAT - TOTAL_VAT - transactionDetails?.pricePerReservation}
                                  totalVat={TOTAL_VAT} totalIncludingVAT={TOTAL_INCLUSIVE_VAT}
                                  totalCostYourBooking={reservType?.totalCost + totalSum}
                                  GuestDetailsData={GuestDetailsData} extraFieldsData={extraFieldsData}
                                  setAddFields={setAddFields} setGuestFields={setGuestFields}
                                  selectedCombination={selectedCombination}
                                  combinationUpsale={selectedCombinationUpsale}
                                  setCombinationUpsale={setSelectedCombinationExtraProduct}
                                  total_product_price={TOTAL_PRODUCT_PRICE}
                                  // ── autofill props ──────────────────────
                                  setFieldTouched={setFieldTouched}
                                  setFieldValue={setFieldValue}
                                  validateForm={validateForm}
                                />
                              );

                            case languageWords?.ConfirmandPay || "Confirm":
                              return <></>;

                            default:
                              return <div>No page found</div>;
                          }
                        })()}
                      </Box>
                    </React.Fragment>
                  )}
                </div>
              </Box>
            </Box>
          );
        }}
      </Formik>

      <Box sx={{ width: 500 }}>
        <Snackbar
          anchorOrigin={{ vertical, horizontal }}
          open={open}
          onClose={handleClosebar}
          message="Reservation creation Unsuccessful"
          key={vertical + horizontal}
        />
      </Box>
    </>
  );
}




